import { createFileRoute } from '@tanstack/react-router';
import {
  AwayBriefing,
  BattleGrid,
  CaptionStrip,
  DiceTray,
  LinesChecklist,
  MemorialCard,
  MockFrame,
  PatienceRing,
  PushToTalk,
  TownClock,
} from '@/components/site/mocks';
import type { ReactNode } from 'react';

export const Route = createFileRoute('/_site/how-it-plays')({
  head: () => ({
    meta: [
      { title: 'How Everwake plays — a session from start to cliffhanger' },
      {
        name: 'description',
        content:
          'Walk through an Everwake session: the lobby, the recap, exploration, negotiation, sealed dice, the grid when it arrives, and the cliffhanger at the end.',
      },
      { property: 'og:title', content: 'How Everwake plays' },
      {
        property: 'og:description',
        content:
          'A session from lobby to cliffhanger: lines, sealed dice, negotiation, the grid when the story gets there.',
      },
      { property: 'og:type', content: 'article' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
  }),
  component: HowItPlays,
});

function Step({
  n,
  stepLabel,
  title,
  children,
  mock,
}: {
  n: string;
  stepLabel: string;
  title: string;
  children: ReactNode;
  mock?: ReactNode;
}) {
  return (
    <section className="grid gap-8 border-t border-border/70 py-14 md:grid-cols-[1fr_1fr] md:items-center">
      <div>
        <p className="rule-line">
          {stepLabel} {n}
        </p>
        <h2 className="mt-3 font-display text-3xl">{title}</h2>
        <div className="mt-4 space-y-3 text-muted-foreground">{children}</div>
      </div>
      <div>{mock}</div>
    </section>
  );
}

function HowItPlays() {
  return (
    <div className="mx-auto max-w-5xl px-5 py-16">
      <p className="rule-line">How it plays</p>
      <h1 className="mt-4 max-w-2xl font-display text-4xl md:text-5xl">
        Two hours, around ninety spoken turns, one arc.
      </h1>
      <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
        A session is a block of time, shaped into an arc by a deterministic director. You speak, the world answers in about a second, and what you did is written down where the game master always remembers.
      </p>

      <Step
        n="1"
        stepLabel="Step"
        title="Before minute zero"
        mock={
          <div className="space-y-4">
            <LinesChecklist />
            <PushToTalk label="Voice on — push to talk" />
          </div>
        }
      >
        <p>
          Pick a campaign, pick a game master, build your character, choose voice or text, and draw your lines. A checklist of fifteen content tags shapes the story inside the boundaries you choose.
        </p>
      </Step>

      <Step n="2" stepLabel="Step" title="Minute zero" mock={<TownClock />}>
        <p>
          A "previously on" recap, the town clock, fresh rumours. Then the game master stops talking and waits for you.
        </p>
        <p>
          The heads-up display stays quiet at the edge: where people are, which exits you have, what is in your purse, and the one line of what you were trying to do.
        </p>
      </Step>

      <Step
        n="3"
        stepLabel="Step"
        title="Most of the session is talk and consequence"
        mock={
          <MockFrame label="Caption strip">
            <div className="space-y-4">
              <CaptionStrip speaker="You">
                I lower my voice. Say that again, slowly.
              </CaptionStrip>
              <CaptionStrip speaker="Jack, game master">
                He chooses a silent path, looking at the door instead, and you learn something from that.
              </CaptionStrip>
            </div>
          </MockFrame>
        }
      >
        <p>
          Exploration, dialogue, consequence. Checking a price, calming a rival, chasing a rumour to its source. Whisper near enemies and stealth favours you; shout a demand and persuasion becomes intimidation.
        </p>
        <p>
          Tension rises and falls in peaks the fiction decides: a confrontation, a chase, a negotiation, a discovery, a dilemma.
        </p>
      </Step>

      <Step
        n="4"
        stepLabel="Step"
        title="Negotiation is a real machine"
        mock={<PatienceRing />}
      >
        <p>
          Positions, levers you can play once, patience that runs out, and your actual argument — assessed and written into the log before the roll, where it moves the die.
        </p>
        <p>
          The game master honors the agreements reached at the table.
        </p>
      </Step>

      <Step
        n="5"
        stepLabel="Step"
        title="Every roll is sealed"
        mock={<DiceTray />}
      >
        <p>
          The seed hash is published before play and the seed revealed when the session ends, so every die can be recomputed afterwards. Most rolls run on Auto; death saves default to your own hand.
        </p>
        <p>
          Click, drag, tap, shake or just say "roll" — the die on screen lands on the result the server already committed to.
        </p>
      </Step>

      <Step
        n="6"
        stepLabel="Step"
        title="Combat, when it happens"
        mock={<BattleGrid />}
      >
        <p>
          The grid slides in and it is the room you were already standing in, with the people who were actually there. Initiative, an action bar, line of sight and cover, enemies acting on their own tactics.
        </p>
        <p>
          Results are spoken as short engine lines first, then narrated. Combat is deliberately turn-based and push-to-talk.
        </p>
      </Step>

      <Step
        n="7"
        stepLabel="Step"
        title="Death is real"
        mock={<MemorialCard />}
      >
        <p>
          Death saves follow the rules exactly, making every outcome honest and final. A clear warning gives you agency before an unchosen deadly beat.
        </p>
        <p>
          If you die, the campaign, the journal and the Codex go on, and a successor inherits part of your standing.
        </p>
      </Step>

      <Step
        n="8"
        stepLabel="Step"
        title="The end, and the gap"
        mock={<AwayBriefing />}
      >
        <p>
          Sessions end on a cliffhanger. Between them, one to three world days pass — up to a full week — and the world moves while you are away in ways you can trace back to a cause.
        </p>
        <p>
          When you sit down again, you get a briefing on what changed while you were away.
        </p>
      </Step>

      <section className="border-t border-border/70 py-14">
        <h2 className="font-display text-3xl">And the Guia, free, any time</h2>
        <p className="mt-4 max-w-2xl text-muted-foreground">
          The Guia (say "GHEE-ah") is an out-of-character assistant that explains rules, your sheet and the dice log. It functions purely as a guide, keeping the game's economy, secrets, and rolls sacred.
        </p>
      </section>
    </div>
  );
}
